/**
 * Workspace Service - manages business workspaces
 */

import type { Workspace, WorkspaceSettings, ModuleName } from '../types';
import {
  getStore,
  addEntity,
  getEntity,
  getAllEntities,
  updateEntity,
} from '../store';
import { generateId } from '../id';

const DEFAULT_MODULES: ModuleName[] = ['notes', 'tasks', 'crm', 'insights', 'assistant'];

export interface CreateWorkspaceInput {
  name: string;
  businessType: string;
  description: string;
  settings?: Partial<WorkspaceSettings>;
}

export function createWorkspace(input: CreateWorkspaceInput): Workspace {
  const now = new Date();
  const workspace: Workspace = {
    id: generateId('ws'),
    name: input.name,
    businessType: input.businessType,
    description: input.description,
    settings: {
      timezone: input.settings?.timezone ?? 'Asia/Seoul',
      currency: input.settings?.currency ?? 'KRW',
      language: input.settings?.language ?? 'ko',
      modules: DEFAULT_MODULES.map((name) => ({
        name,
        enabled: true,
        config: {},
      })),
    },
    createdAt: now,
    updatedAt: now,
  };

  return addEntity(getStore().workspaces, workspace);
}

export function getWorkspace(id: string): Workspace | undefined {
  return getEntity(getStore().workspaces, id);
}

export function getAllWorkspaces(): Workspace[] {
  return getAllEntities(getStore().workspaces);
}

export function updateWorkspace(
  id: string,
  updates: Partial<Omit<Workspace, 'id' | 'createdAt'>>
): Workspace | undefined {
  return updateEntity(getStore().workspaces, id, updates);
}

export function enableModule(workspaceId: string, moduleName: ModuleName): boolean {
  const workspace = getWorkspace(workspaceId);
  if (!workspace) return false;

  const moduleConfig = workspace.settings.modules.find((m) => m.name === moduleName);
  if (moduleConfig) {
    moduleConfig.enabled = true;
  } else {
    workspace.settings.modules.push({ name: moduleName, enabled: true, config: {} });
  }
  updateWorkspace(workspaceId, { settings: workspace.settings });
  return true;
}

export function disableModule(workspaceId: string, moduleName: ModuleName): boolean {
  const workspace = getWorkspace(workspaceId);
  if (!workspace) return false;

  const moduleConfig = workspace.settings.modules.find((m) => m.name === moduleName);
  if (moduleConfig) {
    moduleConfig.enabled = false;
    updateWorkspace(workspaceId, { settings: workspace.settings });
  }
  return true;
}
